import * as XLSX from 'xlsx-js-style';
import api from '../../../auth/apiClient';
import { isAxiosError } from 'axios';
import { trackDownload } from '../../../activity';

interface MonitoringInspeksiHarianProps {
    selectedMonth: string;
    selectedEstate: number | null;
    setIsLoading: (loading: boolean) => void;
    toast: any;
}

const MonitoringInspeksiMAMBHarian = {
    export: async ({ selectedMonth, selectedEstate, setIsLoading, toast }: MonitoringInspeksiHarianProps) => {

        try {
            const params: any = { month: selectedMonth };
            if (selectedEstate) {
                params.estate_id = selectedEstate;
            }

            const response = await api.get('/api/inspection/report-ins-mt-mb', { params });

            const columnOrder = [
                'LEVEL',
                'JABATAN',
                'NIP',
                'NAMA',
                'DIVISI',
                'JENIS INSPEKSI',
                'SATUAN',
                'JUMLAH HARI INSPEKSI',
                'TARGET/HARI',
                'TOTAL',
                'PENCAPAIAN',
                ...Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'))
            ];

            // Kelompokkan berdasarkan estate
            const groupedByEstate = response.data.reduce((acc: any, item: any) => {
                if (!acc[item.estate]) acc[item.estate] = [];
                acc[item.estate].push(item);
                return acc;
            }, {});

            const excelData: any[] = [];

            Object.entries(groupedByEstate).forEach(([estate, items]) => {
                // Baris judul estate
                const estateRow: any = { LEVEL: `${estate}` };
                excelData.push(estateRow);

                (items as any[]).forEach((item: any) => {
                    const row: any = {};
                    row['LEVEL'] = item.job_level;
                    row['JABATAN'] = item.job_title;
                    row['NIP'] = item.nip;
                    row['NAMA'] = item.name;
                    row['DIVISI'] = item.division;
                    row['JENIS INSPEKSI'] = item.inspection_type;
                    row['SATUAN'] = item.unit;
                    row['JUMLAH HARI INSPEKSI'] = item.jumlah_hari_inspeksi;
                    row['TARGET/HARI'] = item.target_per_day;
                    row['TOTAL'] = parseFloat(item.average).toLocaleString('id-ID', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    });

                    // Calculate PENCAPAIAN (TOTAL / TARGET/HARI)
                    const total = parseFloat(item.average);
                    const target = parseFloat(item.target_per_day);
                    row['PENCAPAIAN'] = target > 0 ?
                        ((total / target) * 100).toLocaleString('id-ID', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        }) + '%' : '-';

                    for (let day = 1; day <= 31; day++) {
                        const dayStr = String(day).padStart(2, '0');
                        const value = item.daily_counts[dayStr];
                        if (value && value !== "") {
                            row[dayStr] = value;
                        } else {
                            row[dayStr] = '-';
                        }
                    }

                    excelData.push(row);
                });
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData, { header: columnOrder });

            // Inject missing header cells
            for (let c = 0; c < columnOrder.length; c++) {
                const cell = XLSX.utils.encode_cell({ r: 0, c });
                if (!ws[cell]) ws[cell] = { v: columnOrder[c] };
            }

            const rowCount = excelData.length + 1;
            const lastCol = XLSX.utils.encode_col(columnOrder.length - 1);

            ws['!rows'] = [];
            ws['!rows'][0] = { hpt: 30 };
            ws['!cols'] = [
                { wch: 12 }, // LEVEL
                { wch: 25 }, // JABATAN
                { wch: 25 }, // NIP
                { wch: 25 }, // NAMA
                { wch: 25 }, // DIVISI
                { wch: 20 }, // JENIS INSPEKSI
                { wch: 10 }, // SATUAN
                { wch: 20 }, // JUMLAH HARI INSPEKSI
                { wch: 12 }, // TARGET/HARI
                { wch: 10 }, // TOTAL
                { wch: 12 }, // PENCAPAIAN
                ...Array(31).fill({ wch: 15 }), // Daily columns
            ];
            ws['!ref'] = `A1:${lastCol}${rowCount}`;

            const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

            // Header styling
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell = XLSX.utils.encode_cell({ r: 0, c: C });
                if (!ws[cell]) ws[cell] = { v: '' };
                ws[cell].s = {
                    font: { bold: true, color: { rgb: "FFFFFF" } },
                    fill: { type: "pattern", pattern: "solid", fgColor: { rgb: "4A5A33" } },
                    alignment: { horizontal: "center", vertical: "center" },
                    border: {
                        top: { style: "thin", color: { rgb: "000000" } },
                        bottom: { style: "thin", color: { rgb: "000000" } },
                        left: { style: "thin", color: { rgb: "000000" } },
                        right: { style: "thin", color: { rgb: "000000" } }
                    }
                };
            }

            const totalColIndex = columnOrder.indexOf('TOTAL');
            const pencapaianColIndex = columnOrder.indexOf('PENCAPAIAN');
            const jumlahHariInspeksiColIndex = columnOrder.indexOf('JUMLAH HARI INSPEKSI');
            const targetHariColIndex = columnOrder.indexOf('TARGET/HARI');

            // Create merge groups based on NIP
            const mergeGroups: { [key: string]: { start: number, end: number, nip: string } } = {};
            let currentNip = '';

            for (let R = range.s.r + 1; R <= range.e.r; ++R) {
                const nipCell = XLSX.utils.encode_cell({ r: R, c: 2 }); // NIP column
                const nipValue = ws[nipCell]?.v;

                // Skip estate title rows
                const levelCell = XLSX.utils.encode_cell({ r: R, c: 0 });
                const levelValue = ws[levelCell]?.v;
                const estateNames = [
                    'ESTATE LEMATANG MAKMUR',
                    'ESTATE MUSI MAKMUR',
                    'KANTOR BESAR',
                    'KANTOR JAKARTA',
                    'PLASMA ABAB MAKMUR 1',
                    'PLASMA ABAB MAKMUR 2',
                    'TRAKSI',
                    'PMKS',
                    'ESTATE PENUKAL',
                    'ESTATE MAJU BERSAMA',
                    'ESTATE JAZA CHINDO',
                    'ESTATE KARANG SEJAHTERA',
                    'ESTATE BINA TANI',
                    'ESTATE ABADI BERSAMA',
                    'ESTATE HARAPAN MAKMUR',
                    'UNDEFINED'
                ];
                const isEstateRow = typeof levelValue === 'string' && estateNames.some(name => levelValue === `${name}`);

                if (isEstateRow) {
                    if (currentNip && mergeGroups[currentNip] && mergeGroups[currentNip].start < R - 1) {
                        mergeGroups[currentNip].end = R - 1;
                    }
                    currentNip = '';
                    continue;
                }

                if (nipValue && nipValue !== currentNip) {
                    // Save previous merge group if it has multiple rows
                    if (currentNip && mergeGroups[currentNip] && mergeGroups[currentNip].start < R - 1) {
                        mergeGroups[currentNip].end = R - 1;
                    }

                    // Start new merge group
                    currentNip = nipValue;
                    mergeGroups[currentNip] = { start: R, end: R, nip: currentNip };
                } else if (nipValue === currentNip) {
                    // Continue current merge group
                    mergeGroups[currentNip].end = R;
                }
            }

            // Handle last group
            if (currentNip && mergeGroups[currentNip]) {
                mergeGroups[currentNip].end = range.e.r;
            }

            // Apply merges for columns LEVEL, JABATAN, NIP, NAMA, DIVISI (columns 0-4)
            ws['!merges'] ??= [];

            Object.values(mergeGroups).forEach(group => {
                if (group.start < group.end) {
                    for (let col = 0; col <= 4; col++) { // LEVEL, JABATAN, NIP, NAMA, DIVISI
                        ws['!merges']!.push({
                            s: { r: group.start, c: col },
                            e: { r: group.end, c: col }
                        });
                    }
                }
            });

            // Styling untuk seluruh isi data
            for (let R = range.s.r + 1; R <= range.e.r; ++R) {
                const levelCell = XLSX.utils.encode_cell({ r: R, c: 0 });
                const levelValue = ws[levelCell]?.v;
                const estateNames = [
                    'ESTATE LEMATANG MAKMUR',
                    'ESTATE MUSI MAKMUR',
                    'KANTOR BESAR',
                    'KANTOR JAKARTA',
                    'PLASMA ABAB MAKMUR 1',
                    'PLASMA ABAB MAKMUR 2',
                    'TRAKSI',
                    'PMKS',
                    'ESTATE PENUKAL',
                    'ESTATE MAJU BERSAMA',
                    'ESTATE JAZA CHINDO',
                    'ESTATE KARANG SEJAHTERA',
                    'ESTATE BINA TANI',
                    'ESTATE ABADI BERSAMA',
                    'ESTATE HARAPAN MAKMUR',
                    'UNDEFINED'
                ];
                const isEstateRow = typeof levelValue === 'string' && estateNames.some(name => levelValue === `${name}`);

                if (isEstateRow) {
                    if (!ws['!rows']) ws['!rows'] = [];
                    ws['!rows'][R] = { hpt: 30 };
                    // Baris judul estate
                    ws[levelCell].s = {
                        font: { bold: true, color: { rgb: "000000" }, sz: "14" },
                        alignment: { horizontal: "left", vertical: "left" }
                    };
                    continue; // Lewati styling lainnya untuk baris ini
                }

                // Calculate dynamic row height based on maximum line count in daily columns
                let maxLineCountForRow = 1;

                // Set standard row height
                // const standardHeight = 35;

                if (!ws['!rows']) ws['!rows'] = [];
                if (!ws['!rows'][R]) ws['!rows'][R] = {};
                // ws['!rows'][R].hpt = standardHeight;

                // Total column styling (hijau bold) with float format
                const totalCell = XLSX.utils.encode_cell({ r: R, c: totalColIndex });
                if (ws[totalCell]) {
                    ws[totalCell].s = {
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        fill: {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { rgb: "4A5A33" }
                        },
                        alignment: { horizontal: "right", vertical: "center" },
                        border: {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            left: { style: "thin", color: { rgb: "000000" } },
                            right: { style: "thin", color: { rgb: "000000" } }
                        }
                    };
                }

                // PENCAPAIAN column styling (green bold)
                const pencapaianCell = XLSX.utils.encode_cell({ r: R, c: pencapaianColIndex });
                if (ws[pencapaianCell]) {
                    ws[pencapaianCell].s = {
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        fill: {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { rgb: "4A5A33" }
                        },
                        alignment: { horizontal: "right", vertical: "center" },
                        border: {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            left: { style: "thin", color: { rgb: "000000" } },
                            right: { style: "thin", color: { rgb: "000000" } }
                        }
                    };
                }

                // Styling tiap cell lain
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cell = XLSX.utils.encode_cell({ r: R, c: C });
                    if (!ws[cell]) continue;

                    const value = ws[cell].v;
                    const isDateColumn = C >= 11; // Daily columns start from index 11 (after PENCAPAIAN)
                    const isJumlahHariInspeksiColumn = C === jumlahHariInspeksiColIndex;

                    if (isDateColumn) {
                        // Daily columns without wrap text - top and left aligned
                        if (value === '-') {
                            ws[cell].s = {
                                font: { italic: true, color: { rgb: "808080" } },
                                alignment: { horizontal: "left", vertical: "top" },
                                border: {
                                    top: { style: "thin", color: { rgb: "000000" } },
                                    bottom: { style: "thin", color: { rgb: "000000" } },
                                    left: { style: "thin", color: { rgb: "000000" } },
                                    right: { style: "thin", color: { rgb: "000000" } }
                                }
                            };
                        } else {
                            ws[cell].s = {
                                alignment: { horizontal: "left", vertical: "top" },
                                border: {
                                    top: { style: "thin", color: { rgb: "000000" } },
                                    bottom: { style: "thin", color: { rgb: "000000" } },
                                    left: { style: "thin", color: { rgb: "000000" } },
                                    right: { style: "thin", color: { rgb: "000000" } }
                                }
                            };
                        }
                    } else if (isJumlahHariInspeksiColumn) {
                        // JUMLAH HARI INSPEKSI column with wrap text
                        ws[cell].s = {
                            alignment: { horizontal: "left", vertical: "center", wrapText: true },
                            border: {
                                top: { style: "thin", color: { rgb: "000000" } },
                                bottom: { style: "thin", color: { rgb: "000000" } },
                                left: { style: "thin", color: { rgb: "000000" } },
                                right: { style: "thin", color: { rgb: "000000" } }
                            }
                        };
                    } else if (C === targetHariColIndex || C === totalColIndex || C === pencapaianColIndex) {
                        // TARGET/HARI, TOTAL, and PENCAPAIAN - right aligned
                        ws[cell].s = {
                            alignment: { horizontal: "right", vertical: "center" },
                            border: {
                                top: { style: "thin", color: { rgb: "000000" } },
                                bottom: { style: "thin", color: { rgb: "000000" } },
                                left: { style: "thin", color: { rgb: "000000" } },
                                right: { style: "thin", color: { rgb: "000000" } }
                            }
                        };
                        // Special styling for TOTAL and PENCAPAIAN columns (hijau bold) is handled separately above
                    } else {
                        // All other columns - left aligned
                        ws[cell].s = {
                            alignment: { horizontal: "left", vertical: "center" },
                            border: {
                                top: { style: "thin", color: { rgb: "000000" } },
                                bottom: { style: "thin", color: { rgb: "000000" } },
                                left: { style: "thin", color: { rgb: "000000" } },
                                right: { style: "thin", color: { rgb: "000000" } }
                            }
                        };
                    }

                    // Special styling for TARGET/HARI column - add yellow background for 0 values
                    if (C === targetHariColIndex && value === 0) {
                        ws[cell].s.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { rgb: "FFFF00" }
                        };
                    }
                }
            }

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, `Laporan Inspeksi MA-MB ${selectedMonth}`);

            // Generate Excel file
            XLSX.writeFile(wb, `Laporan Inspeksi MA-MB ${selectedMonth}.xlsx`);
            trackDownload('Laporan Inspeksi MA-MB', { month: selectedMonth });

            toast({
                title: 'Success',
                description: 'Laporan inspeksi berhasil diekspor',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            let description = 'Gagal mengekspor laporan inspeksi';

            if (isAxiosError(error) && error.response) {
                description = `Gagal mengekspor laporan: ${error.response.data?.message || error.message}`;
            } else if (error instanceof Error) {
                description = `Gagal mengekspor laporan: ${error.message}`;
            }

            toast({
                title: 'Error',
                description,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw error;
        }
    }
};

export default MonitoringInspeksiMAMBHarian;
